
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import OnboardingFlow from "@/components/onboarding/OnboardingFlow";
import DashboardLayout from "@/components/layout/DashboardLayout";

const CreatePosit = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: userStatus, isLoading } = useQuery({
    queryKey: ["user-posit-status", user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error("User not found");

      // Get user subscription info and posit count
      const { data: userData, error: userError } = await supabase
        .from("users")
        .select("subscription_type, trial_end_date")
        .eq('id', user.id)
        .maybeSingle();

      if (userError) throw userError;

      const { count, error: countError } = await supabase
        .from("posits")
        .select("*", { count: 'exact', head: true })
        .eq('user_id', user.id);

      if (countError) throw countError;

      const isTrialValid = userData?.trial_end_date 
        ? new Date(userData.trial_end_date) > new Date() 
        : false;

      const isPremiumOrTrial = userData?.subscription_type === 'posit_plus' || 
        (userData?.subscription_type === 'trial' && isTrialValid);

      return {
        positCount: count || 0,
        isPremiumOrTrial,
      };
    },
    enabled: !!user
  });

  useEffect(() => {
    if (!isLoading && userStatus) {
      if (!userStatus.isPremiumOrTrial && userStatus.positCount > 0) {
        toast({
          variant: "destructive",
          title: "Free Plan Limit Reached",
          description: "Upgrade to Posit Plus to create multiple posits.",
        });
        navigate("/dashboard/posits");
      } else if (userStatus.isPremiumOrTrial && userStatus.positCount >= 10) {
        toast({
          variant: "destructive",
          title: "Posit Limit Reached",
          description: "You have reached the maximum limit of 10 posits.",
        });
        navigate("/dashboard/posits");
      }
    }
  }, [userStatus, isLoading, navigate, toast]);

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const handlePositCreated = (positId: string) => {
    toast({
      title: "Posit created!",
      description: "You can now start adding content to your Posit.",
    });
    navigate(`/update-posit/${positId}`);
  };

  return (
    <DashboardLayout
      title="Create New Posit"
      description="Set up your Posit to start sharing your knowledge"
    >
      <OnboardingFlow onPositCreated={handlePositCreated} />
    </DashboardLayout>
  );
};

export default CreatePosit;
